import type { ItemData } from "types/layout";

export interface LandingTile {
  id: string;
  href: string;
  color: string;
}

// Presentation only. A tile is a sidebar item, so it inherits the sidebar's permission and
// module-flag gates, its label and its entries. `target` is the entry a tile opens when the
// user may reach it; otherwise the item's own href, then its first permitted entry.
const TILES: readonly { id: string; target?: string; color: string }[] = [
  { id: "sidepanel_sales", target: "sidepanel_sales__invoices", color: "#2563EB" },
  { id: "sidepanel_delivery_orders", target: "sidepanel_delivery_orders__list", color: "#0F766E" },
  { id: "sidepanel_purchases", target: "sidepanel_purchases__bills", color: "#C2410C" },
  {
    id: "sidepanel_purchase_requests",
    target: "sidepanel_purchase_requests__list",
    color: "#6D28D9",
  },
  { id: "sidepanel_expense_claims", target: "sidepanel_expense_claims__list", color: "#BE185D" },
  { id: "sidepanel_products", target: "sidepanel_products__products", color: "#0E7490" },
  {
    id: "sidepanel_fixed_assets",
    target: "sidepanel_fixed_assets__fixed_assets",
    color: "#475569",
  },
  {
    id: "sidepanel_accounting",
    target: "sidepanel_accounting__manual_journal_entries",
    color: "#15803D",
  },
  {
    id: "sidepanel_projects_tasks",
    target: "sidepanel_projects_tasks__projects",
    color: "#B45309",
  },
  { id: "sidepanel_reports", target: "sidepanel_reports__reports", color: "#0369A1" },
  { id: "sidepanel_settings__workflow_board", color: "#EA580C" },
  { id: "sidepanel_settings", target: "sidepanel_settings__general_settings", color: "#334155" },
];

// The helper can leave null entries in the tree (SideMenu skips them the same way).
const findById = (items: ItemData[] | undefined, id: string): ItemData | undefined => {
  for (const item of items ?? []) {
    if (!item) continue;
    if (item.props?.id === id) return item;
    const nested = findById(item.children, id);
    if (nested) return nested;
  }
  return undefined;
};

const firstHref = (items: ItemData[] | undefined): string | undefined => {
  for (const item of items ?? []) {
    const href = item?.props?.href || firstHref(item?.children);
    if (href) return href;
  }
  return undefined;
};

export function buildLandingTiles(sideMenuData: ItemData[]): LandingTile[] {
  return TILES.flatMap(({ id, target, color }) => {
    const item = findById(sideMenuData, id);
    if (!item) return [];
    const href =
      (target && findById(item.children, target)?.props.href) ||
      item.props.href ||
      firstHref(item.children);
    return href ? [{ id, href, color }] : [];
  });
}
